import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { t } from 'i18next';
import debounce from 'lodash/debounce';
import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { ChipFilters } from '../../../../common/ChipFiltersGroup';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ResultFilter } from '../../../../common/ContentFilters/ResultFilter';
import { ResultSort } from '../../../../common/ContentFilters/ResultSort';
import { ErrorBoundary } from '../../../../errors/ErrorBoundary';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useSearchStore } from '../../../../store/useSearchStore';
import { SearchState } from '../../../../store/useSearchStore/searchStore';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import {
  ButtonStyled,
  FilterContainer,
  Filterlabel,
  FiltersFlexStyled,
  MapViewListViewToggleContainer,
  TextStyles,
  filterLabelStyle,
} from './FiltersContainerDesktop.styled';

type FiltersContainerDesktopProps = {
  choosePCP?: boolean;
  enableMapFilterSortCTA: boolean;
  expandedProviderListView?: boolean;
  filterSortScreen: boolean;
  previousLocation: string;
  resultSet: any;
  sectionType: string;
  setExpandedProviderListView: (a: boolean) => void;
  setPreviousLocation: (a: string) => void;
  setNavigateToDirections: (a: boolean) => void;
  suppressPremiumCarePhysician?: boolean;
};

export const FiltersContainerDesktop = ({
  choosePCP,
  enableMapFilterSortCTA,
  expandedProviderListView,
  filterSortScreen,
  previousLocation,
  resultSet,
  sectionType,
  setExpandedProviderListView,
  setPreviousLocation,
  setNavigateToDirections,
  suppressPremiumCarePhysician = false,
}: FiltersContainerDesktopProps) => {
  const [enableMapViewHideOption] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
  ]);

  const { setSearchStore } = useSearchStore(
    useShallow((state: SearchState) => ({
      setSearchStore: state.setSearchStore,
    }))
  );

  const datatestidbutton = expandedProviderListView
    ? 'view-map-toggle-button'
    : 'view-list-toggle-button';
  const buttonIcon = expandedProviderListView ? 'location_on' : 'list';
  const buttonLabel = expandedProviderListView
    ? t('VIEW_LABELS.VIEW_MAP')
    : t('VIEW_LABELS.VIEW_LIST');

  const handleMapListToggleTrack = () => {
    adobeLinkTrackEvent({
      name: expandedProviderListView ? 'map view toggle' : 'list view toggle',
      location: `body:header`,
      type: 'internal',
    });
  };

  const coverageTypeData = useCoverageType();
  const isGEHAV2 = shouldUseGehaV2Styling();
  const additionalResultFilterCss = isGEHAV2
    ? '$core.color.brand.100'
    : '$descriptionBlack';
  const additionalFilterLabelCss = isGEHAV2
    ? { color: '$core.color.brand.100' }
    : {};

  return (
    <FilterContainer>
      <FiltersFlexStyled
        alignItems="flex-start"
        cssProps={{ expandedProviderListView, choosePCP }}
        justify="start"
      >
        <Flex
          css={{
            paddingTop: expandedProviderListView ? '0px' : '0px',
            gap: '40px',
            minWidth: '64%',
            '@media (max-width: 1250px)': {
              minWidth: 'auto',
            },
            '@media (max-width: 760px)': {
              gap: '20px',
            },
          }}
        >
          <Layout.Stack
            alignLayout="center"
            css={{
              marginRight:
                !expandedProviderListView && choosePCP ? '$lg' : '0px',
            }}
            grow
          >
            {!expandedProviderListView && (
              <Filterlabel>
                <Text
                  css={{ ...filterLabelStyle, ...additionalFilterLabelCss }}
                  data-testid="filter-by-heading"
                  id={t('ACCESSIBILITY_LABELS.FILTER_BY')}
                >
                  {t('FILTER_LABELS.FILTER_BY')}
                </Text>
              </Filterlabel>
            )}

            <Flex
              aria-labelledby={t('ACCESSIBILITY_LABELS.FILTER_BY')}
              css={{ gap: '$sm', flexWrap: 'wrap' }}
              role="group"
            >
              <ResultFilter
                color={additionalResultFilterCss}
                coverageTypeData={coverageTypeData}
                icon="filter_list"
                name={t('CONTENT_FILTERS.ALL_FILTERS')}
                sectionType={sectionType}
                size="$md"
                suppressPremiumCarePhysician={suppressPremiumCarePhysician}
                variant="text"
              />
              {expandedProviderListView ? (
                <ErrorBoundary>
                  <ChipFilters
                    enableMapFilterSortCTA={enableMapFilterSortCTA}
                    previousLocation={previousLocation}
                    searchResults={resultSet}
                    sectionType={sectionType}
                    setPreviousLocation={setPreviousLocation}
                    suppressPremiumCarePhysician={suppressPremiumCarePhysician}
                  />
                </ErrorBoundary>
              ) : !filterSortScreen ? (
                <ErrorBoundary>
                  <ChipFilters
                    enableMapFilterSortCTA={enableMapFilterSortCTA}
                    previousLocation={previousLocation}
                    searchResults={resultSet}
                    sectionType={sectionType}
                    setPreviousLocation={setPreviousLocation}
                    suppressPremiumCarePhysician={suppressPremiumCarePhysician}
                  />
                </ErrorBoundary>
              ) : null}
            </Flex>
          </Layout.Stack>

          {!enableMapViewHideOption && (
            <Layout.Stack
              alignLayout="center"
              css={{
                marginRight: '$xl',
              }}
              grow
            >
              <Filterlabel>
                <Text
                  css={{ ...filterLabelStyle, ...additionalFilterLabelCss }}
                  data-testid="sort-by-heading"
                  id="sort-by"
                >
                  {t('FILTER_LABELS.SORT_BY')}
                </Text>
              </Filterlabel>

              <FilterContainer aria-labelledby="sort-by" role="group">
                <ResultSort resultSet={resultSet} sectionType={sectionType} />
              </FilterContainer>
            </Layout.Stack>
          )}
        </Flex>
        {enableMapViewHideOption && (
          <Layout.Stack
            alignLayout="right"
            css={{
              position: 'relative',
              top: !expandedProviderListView ? '12px' : '0px',
            }}
          >
            <MapViewListViewToggleContainer
              cssProps={{ expandedProviderListView }}
              justify="center"
            >
              <ButtonStyled
                data-auto-testid={datatestidbutton}
                data-testid={datatestidbutton}
                onClick={debounce(() => {
                  handleMapListToggleTrack();
                  setExpandedProviderListView(!expandedProviderListView);
                  if (!expandedProviderListView) setNavigateToDirections(false);
                  setSearchStore({
                    toggleView: !expandedProviderListView ? 'list' : 'map',
                  });
                }, 300)}
                size="$md"
                variant="outline"
              >
                <Layout.Group>
                  <IconSymbol icon={buttonIcon} />
                  <Text css={TextStyles}>{buttonLabel}</Text>
                </Layout.Group>
              </ButtonStyled>
            </MapViewListViewToggleContainer>
          </Layout.Stack>
        )}
      </FiltersFlexStyled>
    </FilterContainer>
  );
};
